import React, { useMemo, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  APIProvider, Map, AdvancedMarker, InfoWindow, useAdvancedMarkerRef, useMap,
} from "@vis.gl/react-google-maps";
import { Star, Crown, MapPin, AlertCircle } from "lucide-react";
import VenueCard from "./VenueCard";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_ID || "DEMO_MAP_ID";
const AIRLIE_CENTER = { lat: -20.2685, lng: 148.7180 };

function VenueMarker({ venue, isActive, isOpen, setOpenId }) {
  const [markerRef, marker] = useAdvancedMarkerRef();
  if (!venue.latitude || !venue.longitude) return null;
  const isSponsored = venue.is_sponsored;
  const bg = isActive ? "bg-coastal" : isSponsored ? "bg-sponsored" : "bg-navy";
  const iconColor = isSponsored && !isActive ? "text-navy" : "text-white";
  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={{ lat: venue.latitude, lng: venue.longitude }}
        onClick={() => setOpenId(venue.id)}
        data-testid={`map-pin-${venue.id}`}
        zIndex={isActive ? 999 : undefined}
      >
        <div className={`relative flex items-center justify-center transition-all ${isActive ? "scale-125" : "hover:scale-110"}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-white ${bg} transition-colors`}>
            {isSponsored && !isActive ? <Crown className={`w-4 h-4 ${iconColor}`} /> : <MapPin className={`w-4 h-4 ${iconColor}`} />}
          </div>
          <div className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 border-r-2 border-b-2 border-white ${bg} transition-colors`} />
        </div>
      </AdvancedMarker>
      {isOpen && marker && (
        <InfoWindow anchor={marker} onClose={() => setOpenId(null)} maxWidth={280}>
          <div className="p-1 w-64" data-testid={`map-popup-${venue.id}`}>
            {venue.places_photo_url || venue.images?.[0] ? (
              <div className="aspect-[16/9] rounded-lg overflow-hidden mb-2">
                <img src={venue.places_photo_url || venue.images[0]} alt={venue.name} className="w-full h-full object-cover" />
              </div>
            ) : null}
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="font-heading text-base text-navy line-clamp-1">{venue.name}</div>
              <div className="flex items-center gap-1 shrink-0 text-sm">
                <Star className="w-3.5 h-3.5 fill-sponsored text-sponsored" />
                <span className="font-medium">{(venue.rating || 0).toFixed(1)}</span>
              </div>
            </div>
            <div className="text-xs text-neutral-500 capitalize mb-2">{venue.category} · {venue.location}</div>
            <p className="text-xs text-neutral-600 line-clamp-2 mb-2">{venue.description}</p>
            <Link
              to={`/venue/${venue.id}`}
              className="text-xs font-medium text-navy hover:underline"
              data-testid={`map-popup-link-${venue.id}`}
            >
              See details →
            </Link>
          </div>
        </InfoWindow>
      )}
    </>
  );
}

/**
 * Auto-fits the map to show all venues with coordinates, whenever the venue list
 * changes. Uses google.maps.LatLngBounds through the imperative map instance.
 */
function AutoFit({ venues }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps || !venues.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    venues.forEach((v) => bounds.extend({ lat: v.latitude, lng: v.longitude }));
    map.fitBounds(bounds, 80);
  }, [map, venues]);
  return null;
}

function NoKeyFallback({ venuesWithCoords }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-navy/20 bg-white/60 p-12 text-center" data-testid="map-no-key">
      <AlertCircle className="w-10 h-10 text-navy/40 mx-auto mb-4" />
      <h3 className="font-heading text-xl text-navy mb-2">Map view ready — needs a Google Maps API key</h3>
      <p className="text-neutral-500 max-w-md mx-auto mb-4">
        Add your key to <code className="bg-neutral-100 px-1.5 py-0.5 rounded">REACT_APP_GOOGLE_MAPS_API_KEY</code>.
      </p>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-2 max-w-2xl mx-auto">
        {venuesWithCoords.slice(0, 6).map((v) => (
          <div key={v.id} className="text-left text-xs bg-white border border-black/5 rounded-lg p-3">
            <div className="font-medium text-navy line-clamp-1">{v.name}</div>
            <div className="text-neutral-500">{v.latitude?.toFixed(4)}, {v.longitude?.toFixed(4)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Split-screen Directory view: scrollable venue list on the left, sticky
 * Google Map on the right. Hovering a card highlights its map pin, and vice
 * versa. Mobile: stacks to map-on-top, list-below.
 */
export default function SplitDirectoryView({ venues }) {
  const [openId, setOpenId] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const listRef = useRef(null);
  const activeId = openId || hoverId;
  const venuesWithCoords = useMemo(() => venues.filter((v) => v.latitude && v.longitude), [venues]);

  // Scroll active card into view when a pin is opened via the map.
  useEffect(() => {
    if (!openId || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-card-id="${openId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [openId]);

  const noKey = !API_KEY || API_KEY === "YOUR_GOOGLE_MAPS_API_KEY";

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="split-directory">
      {/* Left: scrolling list */}
      <div
        ref={listRef}
        className="lg:col-span-2 space-y-4 lg:max-h-[75vh] lg:overflow-y-auto lg:pr-2"
        data-testid="split-list"
      >
        {venues.length === 0 ? (
          <div className="text-center py-20 text-neutral-500">No venues match your filters.</div>
        ) : venues.map((v, i) => (
          <div
            key={v.id}
            data-card-id={v.id}
            onMouseEnter={() => setHoverId(v.id)}
            onMouseLeave={() => setHoverId((cur) => (cur === v.id ? null : cur))}
            className={`transition-shadow rounded-2xl ${activeId === v.id ? "ring-2 ring-coastal" : ""}`}
            data-testid={`split-card-${v.id}`}
          >
            <VenueCard venue={v} index={i} />
          </div>
        ))}
      </div>

      {/* Right: sticky map */}
      <div className="lg:col-span-3 order-first lg:order-last">
        <div className="rounded-2xl overflow-hidden border border-black/5 shadow-sm lg:sticky lg:top-24 h-[50vh] lg:h-[75vh]" data-testid="split-map">
          {noKey ? (
            <NoKeyFallback venuesWithCoords={venuesWithCoords} />
          ) : (
            <APIProvider apiKey={API_KEY}>
              <Map
                defaultCenter={AIRLIE_CENTER}
                defaultZoom={14}
                minZoom={11}
                mapId={MAP_ID}
                gestureHandling="greedy"
                disableDefaultUI={false}
                mapTypeControl={false}
                streetViewControl={false}
                fullscreenControl={true}
                style={{ width: "100%", height: "100%" }}
              >
                <AutoFit venues={venuesWithCoords} />
                {venuesWithCoords.map((v) => (
                  <VenueMarker
                    key={v.id}
                    venue={v}
                    isActive={activeId === v.id}
                    isOpen={openId === v.id}
                    setOpenId={setOpenId}
                  />
                ))}
              </Map>
            </APIProvider>
          )}
        </div>
      </div>
    </div>
  );
}
